import React, {useRef, useEffect} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  LinearProgress
} from '@mui/material';
import { DetailPopup, Title, Text } from 'shared/components';
import { v4 as uuidv4 } from 'uuid';
import {
  Settings as SettingsIcon,
  FileCopy as FileCopyIcon,
  GetApp as GetAppIcon,
  MoreVert as MoreVertIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import PropTypes from 'prop-types';
import { parseCubeData, parseDetailListToCsv } from '../../utils/cube/parsers';
import { getCubeQuery } from '../../utils/cube/queryBuilder';
import CardSettings from '../CardSettings';
import { BeneficiariesCard } from '../BeneficiariesCard';
import { DonationsCard } from '../DonationsCard';
import { ReportingCard } from '../ReportingCard';
import { DataQualityCard } from '../DataQualityCard';
import { ImpactsCard } from '../ImpactsCard';
import DashboardQuery from './DashboardQuery';
import useStyles from './styles';
import DonationsDummy from '../../utils/images/DonationsDummy.png';
import BeneficiariesDummy from '../../utils/images/BeneficiariesDummy.png';
import ReportingDummy from '../../utils/images/ReportingDummy.png';
import DataQualityDummy from '../../utils/images/DataQualityDummy.png';
import ImpactsDummy from '../../utils/images/ImpactsDummy.png';
import { Content, Header } from './styles';
import { getAllDetails } from '../../actions';

const TEMPLATES = [
  {
    id: uuidv4(),
    title: 'Impacts',
    description:
      'Impacts are the impacts your funded programs are making on the world.  You will be able to view these impacts by the number ' +
        'of impacts you can claim (i.e. that your donations have paid for), by social ROI, and by how much you are leveraging ' +
        'foundational investments.  You will also be able to view these by year, portfolio, or portfolio manager, and drill down to ' +
        'specific nonprofits and programs in those silos.',
    src: ImpactsDummy
  },
  {
    id: uuidv4(),
    title: 'Donations',
    description:
      'Donations are the investments in cash, time, goods, and services that you make in your nonprofits funded programs.  You will ' +
        'be able to see these donations by portfolio, by service, and by portfolio manager, as well as compare how donations across ' +
        'these silos are changing over time, as well as drill down to specific nonprofits and programs in those silos.',
    src: DonationsDummy
  },
  {
    id: uuidv4(),
    title: 'Reporting Compliance',
    description:
      'Reporting Compliance is a metric that tells you how many of your funded programs are reporting on time, with a comparison and ' +
        'drilldown capability to see which nonprofits and programs are lagging behind in the reporting process.',
    src: ReportingDummy
  },
  {
    id: uuidv4(),
    title: 'Data Quality',
    description:
      'Data quality tells you how rigorously your nonprofits are measuring the impact data they are providing to you.  A percentage ' +
        'metric tells you exactly how well the different portfolios, services, and managers are being measured, and how they are ' +
        'changing over time.  You will also be able to drill down by silo to see which nonprofits and reports are driving those ' +
        'metrics.',
    src: DataQualityDummy
  },
  {
    id: uuidv4(),
    title: 'Beneficiaries',
    description:
      'Beneficiaries tell you which beneficiary groups are benefiting from your donations, and how those groups distribute by silos, ' +
        'including NPO, portfolio, portfolio manager and service.  You will be able to compare over time, as well as drill down to ' +
        'identify which NPOs and programs are reaching which beneficiaries.',
    src: BeneficiariesDummy
  }
];

const CARD_TEMPLATE = {
  DONATIONS: 'Donations',
  BENEFICIARIES: 'Beneficiaries',
  REPORTING: 'Reporting Compliance',
  DATA: 'Data Quality',
  IMPACTS: 'Impacts'
};

const defaultSettings = {
  metric: 'Report Count',
  segment: 'NPO',
  filter: '',
  year: '',
  beneficiaries: [],
  switch: '',
  compare: '',
  comparisonyear: ''
};

const DashboardCard = ({
  title,
  selectedCard,
  onDelete,
  onClone,
  onUpdateSettings,
  onAddCard,
}) => {
  const dispatch = useDispatch();
  const csvInstance = useRef();
  const classes = useStyles();
  const [showSettings, setshowSettings] = React.useState(false);
  const [showDetails, setshowDetails] = React.useState(false);
  const [showCloneDialog, setShowCloneDialog] = React.useState(false);
  const [memuAnchor, setMenuAnchor] = React.useState(null);
  const [cardExpanded, setCardExpanded] = React.useState({visible:false});
  const beneficiarieList = useSelector(state => state.beneficiaries.list);
  const { cubeTokenReady, totals, accountId } = useSelector(state => {
    const { me } = state.profile;
    return {
      cubeTokenReady: !!(me && me.cubejsToken),
      totals: state.totals,
      accountId: me && me.accountId
    };
  });
  
  const { allList: list, impactDetails, loading: loadingList } = useSelector(state => state.details);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [openDetailPopup, setOpenDetailPopup] = React.useState(false);

  useEffect(() => {
    if (list && csvInstance.current && csvInstance.current.link) {
      setTimeout(() => {
        csvInstance.current.link.click();
      });
    }
  }, [list]);

  const handleClick = event => {
    setMenuAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setMenuAnchor(null);
  };

  const toggleSettings = () => {
    setshowSettings(!showSettings);
  };

  const toggleDetails = () => {
    setshowDetails(!showDetails);
  };

  const handleSettings = e => {
    e.stopPropagation();
    handleClose();
    toggleSettings();
  };

  const handleDetails = () => {
    toggleDetails();
  };

  const handleOnDelete = cardId => {
    onDelete(cardId);
  };

  const handleOnSaveCardSettings = (card, form) => {
    const newCard = { ...card };
    newCard.settings = form;
    onUpdateSettings(newCard);
  };

  const handleOpenCloneDialog = () => {
    handleClose();
    setShowCloneDialog(true);
  };

  const handleCloseCloneDialog = () => {
    setShowCloneDialog(false);
  };

  const handleOnCloseSettings = () => {
    setSelectedTemplate(null);
  };

  const handleOnSaveSettings = (card, form) => {
    const newCard = { ...card };
    newCard.settings = form;
    setSelectedTemplate(null);
    onAddCard(newCard);
  };

  const handleCloseModal = () => {
    setOpenDetailPopup(!openDetailPopup);
  };

  const handleClone = (reset = false) => {
    const cardClone = { ...selectedCard, id: uuidv4() };
    
    if (reset) {
      cardClone.settings = defaultSettings;
      const template = TEMPLATES.filter(template => template.title === cardClone.title);
      setOpenDetailPopup(!openDetailPopup);
      setSelectedTemplate(template[0]);
    } else {
      onClone(cardClone);
    }
    handleCloseCloneDialog();
    window.scrollTo(0, 0);
  };

  const handleDownloadDetails = async () => {
    await dispatch(getAllDetails({ accountId }));
  };

  const query = getCubeQuery(selectedCard);
  const hasQuery = !!query;

  if (!cubeTokenReady) {
    return null;
  }

  const getMenu = (csv, data, hasData, cardTitle) => {
    if (
      (cardTitle === 'Reporting Compliance' || cardTitle === 'Data Quality') &&
      data
    ) {
      hasData = true;
    }
    if (cardTitle == 'Data Quality' && data.total === 0 && data.nonGuess === 0) {
      hasData = false;
    }
    if (cardTitle == 'Reporting Compliance' && data.total === 0 && data.completed === 0) {
      hasData = false;
    }
    if (cardTitle == 'Donations' && data.total === 0) {
      hasData = false;
    }
    return (
      <Menu
        anchorEl={memuAnchor}
        open={Boolean(memuAnchor)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleSettings} className={classes.menuItem}>
          <SettingsIcon className={classes.menuItem} />
          <Title variant="subtitle2" className={classes.menuItemText}>
            Settings
          </Title>
        </MenuItem>
        <MenuItem onClick={handleOpenCloneDialog} className={classes.menuItem}>
          <FileCopyIcon className={classes.menuItem} />
          <Title variant="subtitle2" className={classes.menuItemText}>
            Clone
          </Title>
        </MenuItem>
        { hasData && (<MenuItem onClick={handleDownloadDetails} className={classes.menuItem}>
          <GetAppIcon className={classes.menuItem} />
          <Title variant="subtitle2" className={classes.menuItemText}>
            Download
          </Title>
          {loadingList && (<LinearProgress variant="query" style={{ 
            position: 'absolute',
            width: '100%',
            left: 0,
            top: '39px'
          }}/> )}
        </MenuItem>
        )}
        {hasData && csv.data.length > 0 && (
        <CSVLink
          ref={csvInstance} 
          className={classes.csvLink}
          filename={`${selectedCard.title}-report.csv`}
          data={csv.data}
          headers={csv.headers}
        />
        )}
      </Menu>
    );
  };

  const getCardTemplate = (data, comparisonData, showSettings, expanded) => {
    switch (title) {
      case CARD_TEMPLATE.BENEFICIARIES:
        return (
          <BeneficiariesCard
            years={data.years}
            total={data.total}
            title={data.name}
            blocks={data.blocks}
            emptyMessage={data.emptyMessage}
            handleDetails={handleDetails}
            comparisonYears={comparisonData && comparisonData.years}
            comparisonBlocks={comparisonData && comparisonData.blocks}
            settings={selectedCard.settings}
            expanded={expanded}
          />
        );
      case CARD_TEMPLATE.DONATIONS:
        return <DonationsCard {...data} settings={selectedCard.settings} expanded={expanded} />;
      case CARD_TEMPLATE.REPORTING:
        return <ReportingCard {...data} settings={selectedCard.settings} expanded={expanded} totals={totals} />;
      case CARD_TEMPLATE.DATA:
        return <DataQualityCard {...data} settings={selectedCard.settings} expanded={expanded} totals={totals}/>;
      case CARD_TEMPLATE.IMPACTS:
        return (
          <ImpactsCard
            {...data}
            card={selectedCard}
            saveCardSettings={handleOnSaveCardSettings}
            showSettings={showSettings}
            expanded={expanded}
          />
        );
      default:
        return (
          <Text style={{ marginTop: 120 }} centered>
            This type of card is under construction
          </Text>
        );
    }
  };

  const handleExpandCard = (data, comparisonData, title) => {
    setCardExpanded({visible: true, data, comparisonData, title});
  };

  const handleCloseCard = () => {
    setCardExpanded({visible: false});
  };

  return (
    <DashboardQuery
      query={query}
      render={({ resultSet, isLoading }) => {
        const queryLoading = hasQuery && (isLoading || isLoading === undefined);

        const data = !queryLoading
          ? parseCubeData({
              resultSet,
              card: selectedCard,
              beneficiaries: beneficiarieList,
              totals
            })
          : [];

        const comparisonData = !queryLoading
          ? parseCubeData({
              resultSet,
              card: selectedCard,
              beneficiaries: beneficiarieList,
              isComparisonCard: true,
              totals
            })
          : {};

        const csv = parseDetailListToCsv(list, impactDetails, title);

        const hasData =
          (data &&
            data.blocks &&
            data.blocks.length > 0 &&
            data.blocks.reduce((total, iter) => total + iter.total, 0) > 0) ||
          (data && data.rows && data.rows.length > 0);

        return (
          <>
          {!selectedTemplate && (
          <Card className={classes.card} elevation={2} id="card-root" onDoubleClick={() => handleExpandCard(data, comparisonData, title)}>
            <CardHeader
              avatar={
                <IconButton
                  className={classes.dragIndicatorIcon}
                  color="primary"
                  component="span"
                >
                  <DragIndicatorIcon />
                </IconButton>
              }
              action={
                showSettings ? null : (
                  <div>
                    <IconButton
                      className={classes.moreHeaderIcon}
                      color="primary"
                      component="span"
                      onClick={handleClick}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </div>
                )
              }
              title={
                <Title centered className={classes.cardHeaderTitle}>
                  { (selectedCard?.settings?.customName) ? `${selectedCard?.settings?.customName} (${title})` : title }
                </Title>
              }
              className={classes.cardHeader}
            />
            {memuAnchor && getMenu(csv, data, hasData, title)}
            <Divider />
            {showSettings && selectedCard && (
              <CardSettings
                card={selectedCard}
                onClose={toggleSettings}
                onDelete={handleOnDelete}
                onSave={handleOnSaveCardSettings}
                showingSettings={showSettings}
              />
            )}
            {queryLoading && (
              <CircularProgress style={{ marginTop: '100px' }} />
            )}
            {!queryLoading &&
              !showDetails &&
              data &&
              getCardTemplate(data, comparisonData, showSettings)}
            {!queryLoading && !showDetails && !data && (
              <Text style={{ marginTop: 120 }} centered>
                This type of card is under construction
              </Text>
            )}
            <Dialog open={showCloneDialog} onClose={handleCloseCloneDialog}>
              <DialogTitle>Clone your Dashboard Card</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  When cloning a card you can copy existing settings along with
                  the tempalte, or use the default settings.
                </DialogContentText>
              </DialogContent>
              <DialogActions style={{ marginBottom: '10px' }}>
                <Button
                  onClick={handleCloseCloneDialog}
                  color="primary"
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleClone(false)}
                  size="small"
                >
                  Copy Settings
                </Button>
                <Button
                  onClick={() => handleClone(true)}
                  color="primary"
                  autoFocus
                  size="small"
                >
                  Create New
                </Button>
              </DialogActions>
            </Dialog>
          </Card>
          )}
          {selectedTemplate && (
            <DetailPopup
              open={openDetailPopup}
              onClose={handleCloseModal}
              insideCard={false}
            >
              <Content>
                <Header>
                  <Title centered>Templates</Title>
                  <Divider />
                </Header>
                {selectedTemplate && (
                  <CardSettings
                    card={selectedTemplate}
                    onDelete={handleOnCloseSettings}
                    onClose={handleOnCloseSettings}
                    onSave={handleOnSaveSettings}
                    positiveButtonText="ADD"
                    negativebuttonText="CANCEL"
                    title="Configure Card"
                    noCloseIcon
                    isNew
                  />
                )}
              </Content>
            </DetailPopup>
          )}
          {cardExpanded.visible && cardExpanded.data && (
            <DetailPopup
              open={cardExpanded.visible}
              onClose={handleCloseCard}
              insideCard={false}
            >
              <Content>
                <Header>
                  <Title centered>{ cardExpanded.title }</Title>
                  <Divider />
                </Header>
                { getCardTemplate(cardExpanded.data, cardExpanded.comparisonData, false, true) }
              </Content>
            </DetailPopup>
          )}
          </>
        );
      }}
    />
  );
};

DashboardCard.propTypes = {
  title: PropTypes.string,
  selectedCard: PropTypes.object,
  onDelete: PropTypes.func,
  onClone: PropTypes.func,
  onUpdateSettings: PropTypes.func,
  onAddCard: PropTypes.func,
};

export default React.memo(DashboardCard);
